import { autoinject } from 'aurelia-framework';
import { PayrazrService } from '../../services/payrazr';
import { State } from '../../services/global-state';
import { Router } from 'aurelia-router';
import { I18N } from 'aurelia-i18n';
import { Language } from '../../models/language';
import { ArrayHelper } from '../../helpers/array-helper';
import { ValidationController, ValidationControllerFactory, ValidationRules, ValidateResult } from 'aurelia-validation';
import { HpfConsumerService, SetConsumerUserPreferredLanguageRequest } from '../../services/hpf-consumer.service';
import { ServiceResponse } from '../../models/service-response';
import { observable } from 'aurelia-binding';
import { PostLoginRouteManager } from '../../helpers/post-login-route-manager';

@autoinject
export class PreferredLanguage {
	validationController: ValidationController;

	languages: Language[];
	languageDescs: string[];
	@observable selectedLanguage: Language;
	showOtherLanguage: boolean = false;
	otherLanguage: string;
	declineOption: Language;
	otherLanguageOption: Language;
	selectALanguageOption: Language;

	constructor(
		private router: Router,
		private payrazrService: PayrazrService,
		private consumerService: HpfConsumerService,
		private globalState: State,
		private i18n: I18N,
		private postLoginRouteManager: PostLoginRouteManager,
		validationControllerFactory: ValidationControllerFactory) {
		this.validationController = validationControllerFactory.createForCurrentScope();

		this.selectALanguageOption = new Language(null, this.i18n.i18next.t('selectALanguage', 'Select a language'));
		this.declineOption = new Language('decline', this.i18n.i18next.t('declineToState', 'Decline to state'));
		this.otherLanguageOption = new Language('other', this.i18n.i18next.t('Other', 'Other'));

		this.configureValidationRules();
	}

	activate() {
		this.payrazrService.getISO6391Languages()
			.then(response => {
				let tempLanguageList: Language[] = [];
				// put default text first in list
				tempLanguageList.push(this.selectALanguageOption);

				// convert response into Language type
				response.forEach(item => {
					tempLanguageList.push(new Language(item.languageCode, item.languageName, item.languageNativeName));
				});

				// move english to second
				let indexOfEnglish = tempLanguageList.findIndex((l) => l.languageCode === 'en');
				ArrayHelper.move(tempLanguageList, indexOfEnglish, 1);

				// Add our custom options
				ArrayHelper.insert(tempLanguageList, this.declineOption, 2);
				tempLanguageList.push(this.otherLanguageOption);

				this.languages = tempLanguageList;
				this.languageDescs = this.languages.map(l => l.getLanguageDropDownName());
			});
	}

	selectedLanguageChanged(newValue: Language, oldValue: Language) {
		if (newValue === null) {
			this.showOtherLanguage = false;
		}
		else if (newValue.languageCode === this.otherLanguageOption.languageCode) {
			this.showOtherLanguage = true;
		}
		else {
			this.showOtherLanguage = false;
		}
	}

	continue() {
		this.validationController.errors = [];

		this.validationController.validate()
			.then(response => {
				if (response.valid) {
					let request = new SetConsumerUserPreferredLanguageRequest();

					if (this.selectedLanguage.languageCode === this.declineOption.languageCode) {
						request.declinedToState = true;
					}
					else if (this.selectedLanguage.languageCode === this.otherLanguageOption.languageCode) {
						request.otherLanguage = this.otherLanguage;
						request.declinedToState = false;
					}
					else {
						request.languageCode = this.selectedLanguage.languageCode;
						request.declinedToState = false;
					}

					this.consumerService.setConsumerUserPreferredLanguage(request)
						.then((serviceResponse: ServiceResponse) => {
							if (!serviceResponse.isSuccessful) {
								this.validationController.errors.push(new ValidateResult(null, null, null, false, 'Unable to save preferred language'));
								return;
							}

							this.globalState.customer.preferredLanguageSettings.preferredLanguageCode = request.languageCode;
							this.globalState.customer.preferredLanguageSettings.declinedToStatePreferredLanguage = request.declinedToState;
							this.globalState.customer.preferredLanguageSettings.otherPreferredLanguage = request.otherLanguage;

							let routeResponse = this.postLoginRouteManager.calculateRoute();
							if (routeResponse.routeSettings) {
								this.router.currentInstruction.config.settings = routeResponse.routeSettings;
							}
							this.router.navigateToRoute(routeResponse.route);
						})
						.catch(error => {
							this.validationController.errors.push(new ValidateResult(null, null, null, false, 'Unable to save preferred language'));
						});
				}
			});
	}

	configureValidationRules() {
		this.validationController.addObject(this);
		let self = this;
		ValidationRules
			.ensureObject()
			.satisfies((x: PreferredLanguage) => self.selectedLanguage != null || self.otherLanguage != null)
			.withMessage(this.i18n.i18next.t('languageSelectionIsRequired', 'Language selection is required'))

			.ensure((x: PreferredLanguage) => x.selectedLanguage)
			.satisfies((x: PreferredLanguage) => self.otherLanguage != null && self.otherLanguage !== '')
			.when((x: PreferredLanguage) => self.selectedLanguage != null && self.selectedLanguage.languageCode === self.otherLanguageOption.languageCode)
			.withMessage(this.i18n.i18next.t('otherLanguageValidationText', 'Other Language is required if Other is selected'))

			.on(this);
	}
}
